import type { SingleQuoteSerializer, IApiResponse } from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

interface IGetQuoteParams {
  quoteUUID: string;
  artIDorSlug: string | number;
}
export const quotesApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getQuote: build.query<SingleQuoteSerializer, IGetQuoteParams>({
      query: params => ({
        url: `/foundation/api/arts/${params.artIDorSlug}/quotes/${params.quoteUUID}`,
      }),
      transformResponse: (response: IApiResponse<SingleQuoteSerializer>) => {
        return response.payload.data;
      },
      providesTags: (_result, _error, params) => [
        { type: TagsType.QUOTE, id: params.quoteUUID },
      ],
    }),

    addLikeToQuoteByUuid: build.mutation<void, IGetQuoteParams>({
      query: params => {
        return {
          url: `/foundation/api/quotes/${params.quoteUUID}/likes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          quotesApi.util.updateQueryData("getQuote", params, draftQuote => {
            const isQuoteDislikedByUser =
              draftQuote.user_reaction === "dislike";
            Object.assign<
              SingleQuoteSerializer,
              Partial<SingleQuoteSerializer>
            >(draftQuote, {
              user_reaction: "like",
              likes_count: draftQuote.likes_count + 1,
              ...(isQuoteDislikedByUser && {
                dislikes_count: draftQuote.dislikes_count - 1,
              }),
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    removeLikeFromQuoteByUuid: build.mutation<void, IGetQuoteParams>({
      query: params => {
        return {
          url: `/foundation/api/quotes/${params.quoteUUID}/likes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          quotesApi.util.updateQueryData("getQuote", params, draftQuote => {
            Object.assign<
              SingleQuoteSerializer,
              Partial<SingleQuoteSerializer>
            >(draftQuote, {
              user_reaction: "no_reaction",
              likes_count: draftQuote.likes_count - 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    addDislikeToToQuoteByUuid: build.mutation<string, IGetQuoteParams>({
      query: params => {
        return {
          url: `/foundation/api/quotes/${params.quoteUUID}/dislikes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          quotesApi.util.updateQueryData("getQuote", params, draftQuote => {
            const isArticleLikedByUser = draftQuote.user_reaction === "like";

            Object.assign<
              SingleQuoteSerializer,
              Partial<SingleQuoteSerializer>
            >(draftQuote, {
              user_reaction: "dislike",
              dislikes_count: draftQuote.dislikes_count + 1,
              ...(isArticleLikedByUser && {
                likes_count: draftQuote.likes_count - 1,
              }),
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    removeDislikeFromQuoteByUuid: build.mutation<string, IGetQuoteParams>({
      query: params => {
        return {
          url: `/foundation/api/quotes/${params.quoteUUID}/dislikes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          quotesApi.util.updateQueryData("getQuote", params, draftQuote => {
            Object.assign<
              SingleQuoteSerializer,
              Partial<SingleQuoteSerializer>
            >(draftQuote, {
              user_reaction: "no_reaction",
              dislikes_count: draftQuote.dislikes_count - 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),
  }),
});
